import React, { useCallback, useMemo } from 'react';

import {
  AssistanceAvailableBackground,
  AssistanceAvailableIcon,
  AssistanceRejectedIcon,
  Center,
} from '@frontend/components';
import { computeCascaderSuggestionFlags } from '@frontend/components/src/components/forms/cascader/assistance/computeCascaderSuggestionFlags';
import { useCascaderState } from '@frontend/components/src/components/forms/cascader/context';
import {
  Control,
  DropdownIndicator,
  IndicatorSeparator,
  Input,
  Placeholder,
  SelectedValueContainer,
  SingleSelectedValue,
} from '@frontend/components/src/components/forms/cascader/subcomponents';
import type { CascaderComponentProps } from '@frontend/components/src/components/forms/cascader/types';
import { AssistanceApprovedIcon } from '@frontend/components/src/components/mediaAndIcons/icons/iconSet';

import type { MaterialCascaderSuggestionOption } from './materialCascaderSuggestionTypes';
import { materialSuggestionCascaderStyles } from './materialSuggestionCascaderStyles';

type P = CascaderComponentProps<MaterialCascaderSuggestionOption>;

const useSuggestionAssistanceFlags = () => {
  const { flatOptions, selectedOption, rootOptions } =
    useCascaderState<MaterialCascaderSuggestionOption>();
  return useMemo(
    () =>
      computeCascaderSuggestionFlags(
        true,
        flatOptions,
        selectedOption,
        rootOptions,
      ),
    [flatOptions, selectedOption, rootOptions],
  );
};

const SUGGESTION_FIELD_ICON_RESERVE_RIGHT = '3.75rem';

const suggestionFieldShowsRightIcons = (flags: {
  hasSuggestions: boolean;
  hasAcceptedSuggestions: boolean;
  hasRejectedSuggestions: boolean;
}) =>
  (flags.hasSuggestions &&
    !flags.hasAcceptedSuggestions &&
    !flags.hasRejectedSuggestions) ||
  flags.hasAcceptedSuggestions ||
  (flags.hasSuggestions &&
    flags.hasRejectedSuggestions &&
    !flags.hasAcceptedSuggestions);

const suggestionAssistanceChromeEnabled = (
  flags: {
    hasSuggestions: boolean;
    hasAcceptedSuggestions: boolean;
    hasRejectedSuggestions: boolean;
  },
  isDisabled: boolean | undefined,
) => !(isDisabled ?? false) && suggestionFieldShowsRightIcons(flags);

export const MaterialSuggestionControl = (props: P['Control']) => {
  const {
    children,
    getCommonProps,
    __css: _propsCss,
    className,
    ...rest
  } = props;
  const { cascaderStyles, isDisabled } =
    useCascaderState<MaterialCascaderSuggestionOption>();
  const flags = useSuggestionAssistanceFlags();
  const suggestionsAssistancePromptActive =
    flags.hasSuggestions &&
    !flags.hasAcceptedSuggestions &&
    !flags.hasRejectedSuggestions;
  const shouldShowAssistancePromptChrome =
    suggestionsAssistancePromptActive && !isDisabled;
  return (
    <Control
      getCommonProps={getCommonProps}
      {...rest}
      __css={{
        ...cascaderStyles.control,
        ...(shouldShowAssistancePromptChrome && { position: 'relative' }),
        ...(shouldShowAssistancePromptChrome && {
          borderColor: 'interaction.suggestion',
        }),
        ...(shouldShowAssistancePromptChrome && { isolation: 'isolate' }),
        ...(shouldShowAssistancePromptChrome && {
          bg: 'transparent',
          background: 'transparent',
        }),
      }}
      className={className ?? 'cascader-control'}
    >
      {shouldShowAssistancePromptChrome ? (
        <AssistanceAvailableBackground zIndex={0} />
      ) : null}
      {children}
    </Control>
  );
};

export const MaterialSuggestionDropdownIndicator = (
  props: P['DropdownIndicator'],
) => {
  const { getCommonProps, __css: _propsCss, className, ...rest } = props;
  const { cascaderStyles, isDisabled } =
    useCascaderState<MaterialCascaderSuggestionOption>();
  const flags = useSuggestionAssistanceFlags();
  const suggestionsAssistancePromptActive =
    flags.hasSuggestions &&
    !flags.hasAcceptedSuggestions &&
    !flags.hasRejectedSuggestions;
  const suggestionChevronActive =
    suggestionsAssistancePromptActive && !isDisabled;
  return (
    <DropdownIndicator
      getCommonProps={getCommonProps}
      {...rest}
      __css={{
        ...cascaderStyles.dropdownIndicator,
        ...(suggestionChevronActive
          ? { color: 'interaction.suggestion' }
          : null),
      }}
      className={className ?? 'cascader-dropdown-indicator'}
    />
  );
};

export const MaterialSuggestionIndicatorSeparator = (
  props: P['IndicatorSeparator'],
) => {
  const { getCommonProps, __css: _propsCss, className, ...rest } = props;
  const { cascaderStyles, isDisabled } =
    useCascaderState<MaterialCascaderSuggestionOption>();
  const flags = useSuggestionAssistanceFlags();
  const suggestionsAssistancePromptActive =
    flags.hasSuggestions &&
    !flags.hasAcceptedSuggestions &&
    !flags.hasRejectedSuggestions;
  const suggestionSeparatorActive =
    suggestionsAssistancePromptActive && !isDisabled;
  return (
    <IndicatorSeparator
      getCommonProps={getCommonProps}
      {...rest}
      __css={{
        ...cascaderStyles.indicatorSeparator,
        ...(suggestionSeparatorActive
          ? { borderColor: 'interaction.suggestion' }
          : null),
      }}
      className={className ?? 'cascader-indicator-separator'}
    />
  );
};

export const MaterialSuggestionSelectedValueContainer = (
  props: P['SelectedValueContainer'],
) => {
  const {
    children,
    getCommonProps,
    __css: _propsCss,
    className,
    ...rest
  } = props;
  const {
    cascaderStyles,
    focusInput,
    isOpen,
    isDisabled,
    onRequestOpen,
    getCommonProps: getCascaderCommonProps,
  } = useCascaderState<MaterialCascaderSuggestionOption>();
  const flags = useSuggestionAssistanceFlags();
  const suggestionsAssistancePromptActive =
    flags.hasSuggestions &&
    !flags.hasAcceptedSuggestions &&
    !flags.hasRejectedSuggestions;
  const shouldShowSuggestionsRejectedIcon =
    !isDisabled &&
    flags.hasSuggestions &&
    flags.hasRejectedSuggestions &&
    !flags.hasAcceptedSuggestions;
  const shouldShowSuggestionsApprovedIcon =
    !isDisabled && flags.hasAcceptedSuggestions;
  const shouldShowSuggestionsAvailableIcon =
    suggestionsAssistancePromptActive && !isDisabled;
  const assistanceIconColor = shouldShowSuggestionsRejectedIcon
    ? 'feature.placeholder'
    : 'alert.assistance';

  const handleClick = useCallback(() => {
    if (isDisabled || getCascaderCommonProps().getIsLoading()) {
      return;
    }
    if (!isOpen) {
      onRequestOpen();
    }
    focusInput();
  }, [isOpen, isDisabled, onRequestOpen, focusInput, getCascaderCommonProps]);

  return (
    <SelectedValueContainer
      getCommonProps={getCommonProps}
      {...rest}
      __css={{
        ...cascaderStyles.selectedValueContainer,
        bg: 'transparent',
      }}
      onClick={handleClick}
      className={className ?? 'cascader-selected-value-container'}
    >
      {children}
      {shouldShowSuggestionsAvailableIcon ||
      shouldShowSuggestionsApprovedIcon ||
      shouldShowSuggestionsRejectedIcon ? (
        <Center
          flexShrink={0}
          justifyContent="flex-end"
          ml="auto"
          position="relative"
          zIndex={2}
          color={assistanceIconColor}
        >
          {shouldShowSuggestionsAvailableIcon ? (
            <AssistanceAvailableIcon
              __css={materialSuggestionCascaderStyles.suggestionsAvailableIcon}
            />
          ) : null}
          {shouldShowSuggestionsApprovedIcon ? (
            <AssistanceApprovedIcon
              __css={materialSuggestionCascaderStyles.suggestionsAvailableIcon}
            />
          ) : null}
          {shouldShowSuggestionsRejectedIcon ? (
            <AssistanceRejectedIcon
              __css={materialSuggestionCascaderStyles.suggestionsRejectedIcon}
            />
          ) : null}
        </Center>
      ) : null}
    </SelectedValueContainer>
  );
};

export const MaterialSuggestionInput = (props: P['Input']) => {
  const { inputRef, getCommonProps } = props;
  const {
    comboboxApi,
    handleInputKeyDown,
    cascaderStyles,
    inputValue,
    setInputValue,
    selectedPath,
    isSearchMode,
    isDisabled,
  } = useCascaderState<MaterialCascaderSuggestionOption>();
  const flags = useSuggestionAssistanceFlags();
  const {
    onKeyDown: onInputKeyDown,
    placeholder: _p,
    defaultValue: _d,
    size: _s,
    ...inputProps
  } = comboboxApi.getInputProps();
  const suggestionAcceptedFieldColor =
    !isDisabled &&
    flags.hasAcceptedSuggestions &&
    !isSearchMode &&
    selectedPath.length > 0
      ? 'feature.suggestion'
      : undefined;
  return (
    <Input
      inputRef={inputRef}
      {...inputProps}
      getCommonProps={getCommonProps}
      __css={{
        ...cascaderStyles.input,
        bg: 'transparent',
      }}
      value={inputValue}
      onKeyDown={(event) => {
        handleInputKeyDown(event);
        onInputKeyDown?.(event);
      }}
      onChange={(event) => setInputValue(event.target.value)}
      flexGrow={selectedPath.length > 0 && !isSearchMode ? 0 : undefined}
      color={suggestionAcceptedFieldColor}
      className="cascader-input"
    />
  );
};

export const MaterialSuggestionSingleSelectedValue = (
  props: P['SingleSelectedValue'],
) => {
  const { selectedOption, getCommonProps, __css: _propsCss, ...rest } = props;
  const { cascaderStyles, isDisabled } =
    useCascaderState<MaterialCascaderSuggestionOption>();
  const flags = useSuggestionAssistanceFlags();
  const reserveForIcons = suggestionAssistanceChromeEnabled(flags, isDisabled);
  return (
    <SingleSelectedValue
      getCommonProps={getCommonProps}
      selectedOption={selectedOption}
      {...rest}
      __css={{
        ...cascaderStyles.singleSelectedValue,
        ...(!(isDisabled ?? false) && flags.hasAcceptedSuggestions
          ? { color: 'feature.suggestion' }
          : null),
        ...(reserveForIcons
          ? {
              left: 1,
              right: SUGGESTION_FIELD_ICON_RESERVE_RIGHT,
              maxWidth: 'none',
              mx: 0,
            }
          : null),
      }}
      className="cascader-single-selected-value"
    />
  );
};

export const MaterialSuggestionPlaceholder = (props: P['Placeholder']) => {
  const { getCommonProps, __css: _propsCss, ...rest } = props;
  const { cascaderStyles, isDisabled } =
    useCascaderState<MaterialCascaderSuggestionOption>();
  const flags = useSuggestionAssistanceFlags();
  const reserveForIcons = suggestionAssistanceChromeEnabled(flags, isDisabled);
  return (
    <Placeholder
      getCommonProps={getCommonProps}
      {...rest}
      __css={{
        ...cascaderStyles.placeholder,
        ...(reserveForIcons
          ? { right: SUGGESTION_FIELD_ICON_RESERVE_RIGHT }
          : null),
      }}
    />
  );
};
